import { AG_GRID_LOCALE_VN } from "@ag-grid-community/locale";
import { Table, Title, Tooltip } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconChevronLeft, IconChevronRight, IconChevronsLeft, IconChevronsRight } from "@tabler/icons-react";
import type {
  CellStyle,
  CellValueChangedEvent,
  ColDef,
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GetRowIdParams,
  ICellRendererParams,
  MenuItemDef,
  ModelUpdatedEvent,
  PaginationChangedEvent,
  RowClassParams,
  RowStyle,
  SelectionChangedEvent,
  SelectionColumnDef,
  SuppressKeyboardEventParams,
} from "ag-grid-community";
import { createPart, ModuleRegistry, themeQuartz } from "ag-grid-community";
import { CellSelectionModule, ClipboardModule, ContextMenuModule } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import * as React from "react";
import { paginationPageSize, paginationPageSizeSelector } from "../../../../_base/_const/_constVar";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import { TABLE_HEAD_HEIGHT } from "../config/returnKinds";
import { findOrderLineBySerial } from "../state/selectors";
import type { PositionOption, ReturnOrderLine, ReturnSerialRow } from "../types";
import FieldSelect from "./FieldSelect";

// Menu chuột phải, chọn vùng ô và copy là tính năng enterprise; đăng ký ngay tại lưới (đăng ký lặp lại không sao)
ModuleRegistry.registerModules([ContextMenuModule, CellSelectionModule, ClipboardModule]);

const C = {
  text: "#1B1F24",
  textSub: "#5C6570",
  muted: "#8A929B",
  empty: "#A3AAB2",
  headBg: "#F6F7F4",
  divider: "#ECEEE9",
  rowLine: "#F0F1EE",
  footBg: "#FAFBF8",
  inputBorder: "#D5D9D2",
  accent: "#1F5FAE",
  accentBg: "#E6EEF8",
  accentBorder: "#C9D6E8",
  noSerialRow: "#F2F7FD",
  childIcon: "#B26A0B",
  childBg: "#FFF8EC",
  childBorder: "#F3D3A6",
  danger: "#B42318",
  dangerBg: "#FDF1F0",
  dangerBorder: "#F2D4D1",
};

const GRID_PART = createPart({
  css: `
.ag-header-cell.rsg-head-select { padding-left: 12px; }
.rsg-head-center .ag-header-cell-label { justify-content: center; }
.ag-header-cell:not(:hover):not(:focus-within):not(.ag-header-cell-filtered) .ag-header-cell-filter-button { opacity: 0; }
.rsg-btn { transition: filter .12s ease; }
.rsg-btn:not(:disabled):hover { filter: brightness(0.96); }
.rsg-btn:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 1px; }
`,
});

/** Tạo một lần ở cấp module, cùng thông số lưới màn xuất bán */
const GRID_THEME = themeQuartz
  .withParams({
    fontFamily: "inherit",
    fontSize: 13,
    dataFontSize: 12.5,
    headerFontSize: 12,
    // Tiêu đề cột chữ đen đậm theo yêu cầu user (05/10/2026)
    headerFontWeight: 700,
    headerTextColor: C.text,
    headerBackgroundColor: C.headBg,
    headerHeight: TABLE_HEAD_HEIGHT,
    rowHeight: 44,
    spacing: 6,
    cellHorizontalPadding: 8,
    iconSize: 15,
    accentColor: C.accent,
    foregroundColor: C.text,
    textColor: C.text,
    backgroundColor: "#FFFFFF",
    borderColor: C.divider,
    wrapperBorder: false,
    wrapperBorderRadius: 0,
    headerRowBorder: { style: "solid", width: 1, color: C.divider },
    rowBorder: { style: "solid", width: 1, color: C.rowLine },
    columnBorder: false,
    headerColumnBorder: false,
    pinnedColumnBorder: { style: "solid", width: 1, color: C.rowLine },
    checkboxBorderRadius: 3,
    valueChangeValueHighlightBackgroundColor: C.accentBorder,
  })
  .withPart(GRID_PART);

const CELL_STYLE: Record<"index" | "sub" | "serial", CellStyle> = {
  index: { color: C.muted },
  sub: { color: C.textSub },
  serial: { fontWeight: "500" },
};

const DEFAULT_COL_DEF: ColDef<ReturnSerialRow> = {
  editable: false,
  filter: false,
  floatingFilter: false,
  minWidth: 40,
};

const SELECTION_COLUMN_DEF: SelectionColumnDef = {
  pinned: "left",
  width: 46,
  minWidth: 46,
  maxWidth: 46,
  resizable: false,
  suppressHeaderMenuButton: true,
  headerClass: "rsg-head-select",
  cellStyle: { paddingLeft: "12px" },
};

const VI_NUMBER = new Intl.NumberFormat("vi-VN");
/** Giá / thành tiền: để trống khi không có giá */
const formatMoney = (value: unknown) => (Number(value) ? VI_NUMBER.format(Number(value)) : "");

const isNoSerialRow = (orderLines: ReturnOrderLine[], row: ReturnSerialRow) =>
  row.isNoSerial === true || findOrderLineBySerial(orderLines, row.serial)?.isNoSerial === true;

/** Phím khi đang ở ô chọn trong ô (gõ lọc, mũi tên, Enter), hoặc Enter / Space trên nút: để phần tử tự xử lý */
const suppressControlKeys = (params: SuppressKeyboardEventParams<ReturnSerialRow>) => {
  const target = params.event.target as HTMLElement | null;
  if (target?.closest(".rsg-cell-select")) return true;
  return target?.tagName === "BUTTON" && (params.event.key === "Enter" || params.event.key === " ");
};

const disabledLook = (off: boolean): React.CSSProperties => (off ? { opacity: 0.45, cursor: "not-allowed" } : {});

const S: Record<string, React.CSSProperties> = {
  wrapper: { width: "100%", display: "flex", flexDirection: "column", minHeight: 0 },
  grid: { flex: "1 1 auto", minHeight: 0 },
  cell: { display: "flex", alignItems: "center", gap: 6, height: "100%", minWidth: 0, lineHeight: "18px" },
  ellipsis: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 },
  badge: {
    flexShrink: 0,
    padding: "1px 6px",
    borderRadius: 5,
    background: C.accentBg,
    color: C.accent,
    fontSize: 10.5,
    fontWeight: 700,
    lineHeight: "16px",
  },
  qtyCell: { display: "flex", alignItems: "center", justifyContent: "flex-end", height: "100%" },
  qtyBox: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-end",
    minWidth: 40,
    height: 28,
    boxSizing: "border-box",
    padding: "0 6px",
    border: `1px solid ${C.accent}`,
    borderRadius: 6,
    background: "#FFFFFF",
    lineHeight: "normal",
    cursor: "text",
  },
  selectWrap: { display: "flex", alignItems: "center", height: "100%", width: "100%" },
  actions: { display: "flex", alignItems: "center", justifyContent: "center", gap: 4, height: "100%" },
  iconButton: {
    width: 28,
    height: 28,
    padding: 0,
    boxSizing: "border-box",
    borderRadius: 6,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  footer: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "8px 14px",
    borderTop: `1px solid ${C.divider}`,
    background: C.footBg,
    color: C.textSub,
    fontSize: 12,
    lineHeight: "18px",
  },
  pager: { display: "flex", alignItems: "center", gap: 10, flexShrink: 0, whiteSpace: "nowrap" },
  pageNav: { display: "flex", alignItems: "center", gap: 4 },
  pageButton: {
    width: 24,
    height: 24,
    padding: 0,
    boxSizing: "border-box",
    border: `1px solid ${C.inputBorder}`,
    borderRadius: 6,
    background: "#FFFFFF",
    color: C.textSub,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  pageSize: {
    height: 24,
    padding: "0 4px",
    border: `1px solid ${C.inputBorder}`,
    borderRadius: 6,
    background: "#FFFFFF",
    color: C.text,
    fontFamily: "inherit",
    fontSize: 12,
    cursor: "pointer",
  },
};

const StrokeIcon = ({ paths, color, size, strokeWidth }: { paths: string[]; color: string; size: number; strokeWidth: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    style={{ flexShrink: 0 }}
  >
    {paths.map((d) => (
      <path key={d} d={d} />
    ))}
  </svg>
);

const ICON_PENCIL = ["M4 20h4L19 9l-4-4L4 16z"];
const ICON_TRASH = ["M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3"];

interface PositionCellProps {
  row: ReturnSerialRow;
  positions: PositionOption[];
  disabled: boolean;
  onChange: (position: PositionOption | null) => void;
}

/** Vị trí kho của serial: ô chọn viền xám; chưa có vị trí thì viền đứt cam "Chọn vị trí" */
const PositionCell = ({ row, positions, disabled, onChange }: PositionCellProps) => {
  const value = row.locatorId != null && row.locatorId !== "" ? String(row.locatorId) : "";
  const current = positions.find((position) => String(position.id) === value);
  if (disabled) {
    const text = current?.label ?? (row.positionName ? `${row.positionCode ?? ""} - ${row.positionName}` : "");
    return (
      <span style={{ ...S.cell, color: text ? C.text : C.empty }}>
        <span style={S.ellipsis}>{text || "—"}</span>
      </span>
    );
  }
  // Vị trí đã lưu không còn trong danh sách của kho hiện tại vẫn hiện để không mất giá trị
  const list: PositionOption[] =
    current || !value
      ? positions
      : [
          {
            id: Number(value),
            code: String(row.positionCode ?? ""),
            name: String(row.positionName ?? ""),
            label: `${row.positionCode ?? ""} - ${row.positionName ?? ""}`,
          },
          ...positions,
        ];
  return (
    <span className="rsg-cell-select" style={S.selectWrap}>
      <FieldSelect
        ariaLabel={`Vị trí kho của serial ${row.serial ?? ""}`}
        value={value}
        placeholder="Chọn vị trí"
        options={list.map((position) => ({
          value: String(position.id),
          label: position.label,
          primary: position.code,
          hint: position.name,
        }))}
        needed={!value}
        compact
        height={28}
        dropdownWidth={340}
        searchable
        title={current?.label}
        onChange={(next) => onChange(positions.find((position) => String(position.id) === next) ?? null)}
      />
    </span>
  );
};

interface StatusCellProps {
  row: ReturnSerialRow;
  statuses: string[];
  disabled: boolean;
  onChange: (status: string) => void;
}

/** Tình trạng serial: ô chọn không viền, hiện viền khi rê chuột như ô sửa của lưới */
const StatusCell = ({ row, statuses, disabled, onChange }: StatusCellProps) => {
  const value = row.serialStatus ?? "";
  if (disabled) {
    return (
      <span style={S.cell}>
        <span style={S.ellipsis}>{value || <span style={{ color: C.empty }}>—</span>}</span>
      </span>
    );
  }
  const list = value && !statuses.includes(value) ? [value, ...statuses] : statuses;
  return (
    <span className="rsg-cell-select" style={S.selectWrap}>
      <FieldSelect
        ariaLabel={`Tình trạng của serial ${row.serial ?? ""}`}
        value={value}
        placeholder="Chọn tình trạng"
        options={list.map((status) => ({ value: status, label: status }))}
        plain
        height={28}
        dropdownWidth={220}
        onChange={onChange}
      />
    </span>
  );
};

interface RowActionsProps {
  serial: string;
  hasChildren: boolean;
  disabled: boolean;
  onOpenChildren: () => void;
  onRemove: () => void;
}

/** Hai nút vuông: serial con và xoá dòng */
const RowActions = ({ serial, hasChildren, disabled, onOpenChildren, onRemove }: RowActionsProps) => {
  const childLabel = hasChildren ? "Thông tin serial con" : "Chưa có serial con";
  return (
    <span style={S.actions}>
      <Tooltip label={childLabel}>
        <button
          type="button"
          className="rsg-btn"
          style={{ ...S.iconButton, border: `1px solid ${C.childBorder}`, background: C.childBg }}
          aria-label={`${childLabel} ${serial}`}
          onClick={onOpenChildren}
        >
          <StrokeIcon paths={ICON_PENCIL} color={C.childIcon} size={14} strokeWidth={2} />
        </button>
      </Tooltip>
      <Tooltip label="Xóa">
        <button
          type="button"
          className="rsg-btn"
          style={{ ...S.iconButton, border: `1px solid ${C.dangerBorder}`, background: C.dangerBg, ...disabledLook(disabled) }}
          disabled={disabled}
          aria-label={`Xóa ${serial}`}
          onClick={onRemove}
        >
          <StrokeIcon paths={ICON_TRASH} color={C.danger} size={14} strokeWidth={2} />
        </button>
      </Tooltip>
    </span>
  );
};

const CHILD_HEAD: React.CSSProperties = { background: C.headBg, color: C.textSub, fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" };

/** Serial con của một serial (chỉ xem; phiếu đổi trả không sửa serial con, như trang cũ) */
const openChildSerials = (row: ReturnSerialRow) =>
  modals.open({
    title: <Title order={4}>Thông tin serial con</Title>,
    radius: "md",
    size: "90%",
    children: (
      <Table.ScrollContainer minWidth={760} mah={480} type="native">
        <Table withTableBorder stickyHeader verticalSpacing={6} fz={12.5}>
          <Table.Thead>
            <Table.Tr>
              {["Serial cha", "Serial con", "Tên sản phẩm", "Đơn vị tính", "Số lượng", "Thời gian bảo hành", "Tình trạng serial con"].map(
                (label) => (
                  <Table.Th key={label} style={CHILD_HEAD}>
                    {label}
                  </Table.Th>
                ),
              )}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(row.tblInventoryChildSerialOnhandCommands ?? []).length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={7} style={{ textAlign: "center", color: C.textSub, padding: 16 }}>
                  Serial {row.serial} chưa có serial con
                </Table.Td>
              </Table.Tr>
            ) : (
              (row.tblInventoryChildSerialOnhandCommands ?? []).map((child, index) => (
                <Table.Tr key={`${child.serial ?? ""}-${index}`}>
                  <Table.Td>{child.parentSerial ?? row.serial}</Table.Td>
                  <Table.Td style={{ fontWeight: 500 }}>{child.serial}</Table.Td>
                  <Table.Td>{child.itemName}</Table.Td>
                  <Table.Td>{child.uomCode}</Table.Td>
                  <Table.Td>{child.quantity}</Table.Td>
                  <Table.Td>{child.warrantyPeriod}</Table.Td>
                  <Table.Td>{child.serialStatus}</Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    ),
  });

interface PageInfo {
  page: number;
  totalPages: number;
  rowCount: number;
}

interface GridFooterProps extends PageInfo {
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  onGoToPage: (page: number) => void;
}

/** Chân lưới: phân trang 25 / 50 / 100 bên phải */
const GridFooter = ({ page, totalPages, rowCount, pageSize, onPageSizeChange, onGoToPage }: GridFooterProps) => {
  const from = rowCount > 0 ? page * pageSize + 1 : 0;
  const to = Math.min((page + 1) * pageSize, rowCount);
  const atFirst = page <= 0;
  const atLast = page >= totalPages - 1;
  return (
    <div style={S.footer}>
      <span style={{ ...S.pager, marginLeft: "auto" }}>
        <span>
          {from} – {to} / {rowCount}
        </span>
        {totalPages > 1 && (
          <span style={S.pageNav}>
            <button type="button" style={{ ...S.pageButton, ...disabledLook(atFirst) }} disabled={atFirst} aria-label="Trang đầu" onClick={() => onGoToPage(0)}>
              <IconChevronsLeft size={14} stroke={2} />
            </button>
            <button type="button" style={{ ...S.pageButton, ...disabledLook(atFirst) }} disabled={atFirst} aria-label="Trang trước" onClick={() => onGoToPage(page - 1)}>
              <IconChevronLeft size={14} stroke={2} />
            </button>
            <span style={{ padding: "0 4px" }}>
              Trang {page + 1} / {totalPages}
            </span>
            <button type="button" style={{ ...S.pageButton, ...disabledLook(atLast) }} disabled={atLast} aria-label="Trang sau" onClick={() => onGoToPage(page + 1)}>
              <IconChevronRight size={14} stroke={2} />
            </button>
            <button type="button" style={{ ...S.pageButton, ...disabledLook(atLast) }} disabled={atLast} aria-label="Trang cuối" onClick={() => onGoToPage(totalPages - 1)}>
              <IconChevronsRight size={14} stroke={2} />
            </button>
          </span>
        )}
        <select aria-label="Số dòng mỗi trang" style={S.pageSize} value={pageSize} onChange={(event) => onPageSizeChange(Number(event.target.value))}>
          {paginationPageSizeSelector.map((size) => (
            <option key={size} value={size}>
              {size} / trang
            </option>
          ))}
        </select>
      </span>
    </div>
  );
};

export interface ReturnSerialGridHandlers {
  onRemove: (serials: string[]) => void;
  onPositionChange: (serial: string, position: PositionOption | null) => void;
  onFieldChange: (serial: string, key: "serialStatus" | "attribute11" | "note", value: string | null) => void;
  onQuantityChange: (serial: string, quantity: number) => void;
  onImportExcel: () => void;
  onExportExcel: () => void;
  onSelectionChange: (rows: ReturnSerialRow[]) => void;
}

interface ReturnSerialGridProps extends ReturnSerialGridHandlers {
  rows: ReturnSerialRow[];
  orderLines: ReturnOrderLine[];
  positions: PositionOption[];
  serialStatuses: string[];
  editDisabled: boolean;
  /** Serial cần cuộn tới và nháy dòng; đổi giá trị (khác null) mới chạy */
  focusSerial: string | null;
}

/**
 * Lưới serial khách trả đã quét. Thứ tự cột theo trang nhập kho cũ (columnDefs của WarehouseInWardActionV2):
 * STT · Mã vị trí · Vị trí kho · Mã hàng · Serial · Tên SP · ĐVT1 · SL1 · Giá nhập · Lý do đổi trả ·
 * Thành tiền · Tình trạng · Ghi chú tình trạng · Ghi chú · Thao Tác. Giao diện theo màn xuất bán.
 */
const ReturnSerialGrid = (props: ReturnSerialGridProps) => {
  const { rows, positions, serialStatuses, editDisabled, focusSerial } = props;
  const gridRef = React.useRef<AgGridReact<ReturnSerialRow>>(null);
  const latest = React.useRef(props);
  latest.current = props;

  const [pageSize, setPageSize] = React.useState(paginationPageSize);
  const [pageInfo, setPageInfo] = React.useState<PageInfo>({ page: 0, totalPages: 0, rowCount: 0 });

  const columnDefs = React.useMemo<ColDef<ReturnSerialRow>[]>(
    () => [
      {
        headerName: "STT",
        colId: "index",
        pinned: "left",
        sortable: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        width: 52,
        minWidth: 52,
        maxWidth: 52,
        cellStyle: CELL_STYLE.index,
        valueGetter: (params) => Number(params?.node?.rowIndex) + 1,
      },
      { field: "positionCode", headerName: "Mã vị trí", filter: "agTextColumnFilter", width: 112, cellStyle: CELL_STYLE.sub },
      {
        field: "locatorId",
        headerName: "Vị trí kho",
        width: 210,
        suppressKeyboardEvent: suppressControlKeys,
        valueGetter: (params) => params.data?.locatorId ?? "",
        cellRenderer: (params: ICellRendererParams<ReturnSerialRow>) =>
          params.data ? (
            <PositionCell
              row={params.data}
              positions={positions}
              disabled={editDisabled}
              onChange={(position) => params.data?.serial && latest.current.onPositionChange(params.data.serial, position)}
            />
          ) : null,
      },
      { field: "itemCode", headerName: "Mã hàng", filter: "agTextColumnFilter", width: 116 },
      { field: "serial", headerName: "Serial", filter: "agTextColumnFilter", width: 132, cellStyle: CELL_STYLE.serial },
      {
        field: "itemName",
        headerName: "Tên SP",
        filter: "agTextColumnFilter",
        flex: 1,
        minWidth: 200,
        cellRenderer: (params: ICellRendererParams<ReturnSerialRow>) => {
          if (!params.data) return null;
          const line = findOrderLineBySerial(latest.current.orderLines, params.data.serial);
          return (
            <span style={S.cell}>
              {line?.isBhmrPackage === true && <span style={S.badge}>BHMR</span>}
              <span style={S.ellipsis} title={params.data.itemName ?? ""}>
                {params.data.itemName}
              </span>
            </span>
          );
        },
      },
      {
        colId: "primaryUomName",
        headerName: "ĐVT1",
        width: 72,
        valueGetter: (params) => params.data?.primaryUomName || params.data?.primaryUomCode || params.data?.primaryUom || "",
      },
      {
        field: "primaryQuantity",
        headerName: "SL1",
        type: "rightAligned",
        width: 72,
        filter: "agNumberColumnFilter",
        cellEditor: "agNumberCellEditor",
        singleClickEdit: true,
        // Chỉ hàng không quản lý serial mới sửa được số lượng, như trang cũ
        editable: (params) => !editDisabled && !!params.data && isNoSerialRow(latest.current.orderLines, params.data),
        cellEditorParams: (params: ICellRendererParams<ReturnSerialRow>) => {
          const line = params.data ? findOrderLineBySerial(latest.current.orderLines, params.data.serial) : undefined;
          return { min: 1, max: line?.primaryQuantity ?? undefined };
        },
        cellRenderer: (params: ICellRendererParams<ReturnSerialRow>) =>
          !editDisabled && params.data && isNoSerialRow(latest.current.orderLines, params.data) ? (
            <span style={S.qtyCell}>
              <span style={S.qtyBox}>{params.value}</span>
            </span>
          ) : (
            params.value ?? null
          ),
      },
      { colId: "poPrice", headerName: "Giá nhập", type: "rightAligned", width: 104, valueGetter: (params) => formatMoney(params.data?.poPrice) },
      { field: "returnReason", headerName: "Lý do đổi trả", filter: "agTextColumnFilter", width: 150, cellStyle: CELL_STYLE.sub },
      {
        colId: "totalAmount",
        headerName: "Thành tiền",
        type: "rightAligned",
        width: 112,
        valueGetter: (params) => formatMoney((Number(params.data?.poPrice) || 0) * (Number(params.data?.primaryQuantity) || 0)),
      },
      {
        field: "serialStatus",
        headerName: "Tình trạng",
        filter: "agTextColumnFilter",
        width: 160,
        suppressKeyboardEvent: suppressControlKeys,
        cellRenderer: (params: ICellRendererParams<ReturnSerialRow>) =>
          params.data ? (
            <StatusCell
              row={params.data}
              statuses={serialStatuses}
              disabled={editDisabled}
              onChange={(status) => params.data?.serial && latest.current.onFieldChange(params.data.serial, "serialStatus", status)}
            />
          ) : null,
      },
      {
        field: "attribute11",
        headerName: "Ghi chú tình trạng",
        editable: !editDisabled,
        cellEditor: "agTextCellEditor",
        filter: "agTextColumnFilter",
        width: 180,
      },
      { field: "note", headerName: "Ghi chú", editable: !editDisabled, cellEditor: "agTextCellEditor", filter: "agTextColumnFilter", width: 180 },
      {
        colId: "action",
        headerName: "Thao Tác",
        headerClass: "rsg-head-center",
        pinned: "right",
        width: 76,
        minWidth: 76,
        maxWidth: 76,
        sortable: false,
        resizable: false,
        suppressHeaderMenuButton: true,
        suppressKeyboardEvent: suppressControlKeys,
        cellRenderer: (params: ICellRendererParams<ReturnSerialRow>) => {
          const row = params.data;
          if (!row) return null;
          const serial = String(row.serial ?? "");
          return (
            <RowActions
              serial={serial}
              hasChildren={(row.tblInventoryChildSerialOnhandCommands?.length ?? 0) > 0}
              disabled={editDisabled}
              onOpenChildren={() => openChildSerials(row)}
              onRemove={() => serial && latest.current.onRemove([serial])}
            />
          );
        },
      },
    ],
    [editDisabled, positions, serialStatuses],
  );

  const onCellValueChanged = React.useCallback((event: CellValueChangedEvent<ReturnSerialRow>) => {
    const serial = event.data?.serial;
    if (!serial) return;
    const colId = event.column.getColId();
    if (colId === "primaryQuantity") {
      const quantity = Number(event.newValue);
      const line = findOrderLineBySerial(latest.current.orderLines, serial);
      if (!quantity || Number.isNaN(quantity) || quantity < 1) {
        NotificationExtension.Warn("Vui lòng nhập số lượng hợp lệ.");
        event.node.setDataValue("primaryQuantity", event.oldValue);
        return;
      }
      if (line && quantity > Number(line.primaryQuantity)) {
        NotificationExtension.Warn("Số lượng vượt quá số lượng cần nhập");
        event.node.setDataValue("primaryQuantity", event.oldValue);
        return;
      }
      latest.current.onQuantityChange(serial, quantity);
    }
    if (colId === "attribute11" || colId === "note") {
      latest.current.onFieldChange(serial, colId, event.newValue ?? null);
    }
  }, []);

  const getContextMenuItems = React.useCallback(
    (params: GetContextMenuItemsParams<ReturnSerialRow>): (DefaultMenuItem | MenuItemDef)[] => {
      const rowNumber = Number(params?.node?.rowIndex) + 1 || "";
      const { editDisabled } = latest.current;
      return [
        {
          name: `Xóa dòng thứ: ${rowNumber}`,
          disabled: editDisabled || !params.node?.data,
          action: () => params.node?.data?.serial && latest.current.onRemove([params.node.data.serial]),
        },
        "separator",
        { name: "Import Excel", disabled: editDisabled, action: () => latest.current.onImportExcel() },
        { name: "Export Excel", action: () => latest.current.onExportExcel() },
        "separator",
        "copy",
        "copyWithHeaders",
        "copyWithGroupHeaders",
      ];
    },
    [],
  );

  const getRowId = React.useCallback((params: GetRowIdParams<ReturnSerialRow>) => String(params.data.serial), []);

  const getRowStyle = React.useCallback((params: RowClassParams<ReturnSerialRow>): RowStyle | undefined => {
    const row = params.data;
    if (row && isNoSerialRow(latest.current.orderLines, row)) return { backgroundColor: C.noSerialRow };
    return undefined;
  }, []);

  // STT lấy theo rowIndex: làm mới sau khi xoá / sắp xếp / lọc để số thứ tự không bị lệch
  const onModelUpdated = React.useCallback(
    (event: ModelUpdatedEvent<ReturnSerialRow>) => event.api.refreshCells({ columns: ["index"] }),
    [],
  );

  const onPaginationChanged = React.useCallback((event: PaginationChangedEvent<ReturnSerialRow>) => {
    const next: PageInfo = {
      page: event.api.paginationGetCurrentPage(),
      totalPages: event.api.paginationGetTotalPages(),
      rowCount: event.api.paginationGetRowCount(),
    };
    setPageInfo((prev) =>
      prev.page === next.page && prev.totalPages === next.totalPages && prev.rowCount === next.rowCount ? prev : next,
    );
  }, []);

  const onSelectionChanged = React.useCallback(
    (event: SelectionChangedEvent<ReturnSerialRow>) => latest.current.onSelectionChange(event.api.getSelectedRows()),
    [],
  );

  const goToPage = React.useCallback((page: number) => gridRef.current?.api.paginationGoToPage(page), []);

  // Tới dòng được yêu cầu: chuyển trang chứa dòng, cuộn ra giữa rồi nháy dòng
  React.useEffect(() => {
    if (!focusSerial) return undefined;
    let flashTimer: number | undefined;
    const locateTimer = window.setTimeout(() => {
      const api = gridRef.current?.api;
      const node = api?.getRowNode(focusSerial);
      if (!api || !node || node.rowIndex == null) return;
      const page = Math.floor(node.rowIndex / api.paginationGetPageSize());
      if (page !== api.paginationGetCurrentPage()) api.paginationGoToPage(page);
      api.ensureNodeVisible(node, "middle");
      flashTimer = window.setTimeout(() => api.flashCells({ rowNodes: [node], flashDuration: 800, fadeDuration: 1000 }), 120);
    }, 0);
    return () => {
      window.clearTimeout(locateTimer);
      window.clearTimeout(flashTimer);
    };
  }, [focusSerial]);

  return (
    <div style={{ ...S.wrapper, height: "100%" }}>
      <div style={S.grid}>
        <AgGridReact<ReturnSerialRow>
          ref={gridRef}
          rowData={rows}
          getRowId={getRowId}
          getRowStyle={getRowStyle}
          theme={GRID_THEME}
          columnDefs={columnDefs}
          defaultColDef={DEFAULT_COL_DEF}
          selectionColumnDef={SELECTION_COLUMN_DEF}
          cellSelection={true}
          // Ô đang gõ dở (ghi chú, số lượng) được ghi khi bấm ra ngoài, kể cả bấm Lưu
          stopEditingWhenCellsLoseFocus={true}
          onCellValueChanged={onCellValueChanged}
          onModelUpdated={onModelUpdated}
          pagination={true}
          paginationPageSize={pageSize}
          suppressPaginationPanel={true}
          onPaginationChanged={onPaginationChanged}
          localeText={AG_GRID_LOCALE_VN}
          rowSelection={{ mode: "multiRow" }}
          onSelectionChanged={onSelectionChanged}
          getContextMenuItems={getContextMenuItems}
          suppressRowHoverHighlight={true}
          columnHoverHighlight={true}
          // Trang tự vẽ trạng thái trống (lời nhắc đổi theo đã chọn kho hay chưa)
          suppressNoRowsOverlay={true}
        />
      </div>
      <GridFooter
        page={pageInfo.page}
        totalPages={pageInfo.totalPages}
        rowCount={pageInfo.rowCount}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        onGoToPage={goToPage}
      />
    </div>
  );
};

export default React.memo(ReturnSerialGrid);
